import React, { Component } from "react";
import {
  Alert,
  Checkbox,
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import * as XLSX from "xlsx";

import {
  ButtonItem,
  DatePickerItem,
  ModalItem,
  TextAreaItem,
} from "../../../../Components/Items";
import AyudaConversion from "../../../../Components/AyudaConversion";
import SelectInsumosItem from "../../../../Components/SelectInsumosItem";
import SelectProveedoresItem from "../../../../Components/SelectProveedoresItem";
import { extraerLineasDePdf } from "../../../../Components/LeerPdf/extraerLineasDePdf";
import {
  parsearLineasCotizacion,
  detectarRfcs,
} from "../../../../Components/LeerPdf/Cotizacion";

const { Text } = Typography;

const normalizar = (valor) => String(valor ?? "").trim().toUpperCase();

const parseNumero = (valor) => {
  if (valor === undefined || valor === null || valor === "") return null;
  if (typeof valor === "number") return valor;
  const limpio = String(valor).replace(/[^0-9.-]/g, "");
  const numero = parseFloat(limpio);
  return Number.isNaN(numero) ? null : numero;
};

// Encabezados de la plantilla que genera SolicitarCotizacion. Se busca primero por
// igualdad exacta y solo despues por "contiene": si no, "Descripcion" se comeria la
// columna "Descripcion Proveedor" (y "Clave" la de "Codigo Proveedor").
const ALIAS_CLAVE = ["CLAVE", "CLAVE INSUMO", "CLAVE SISTEMA"];
const ALIAS_CODIGO = ["CODIGO PROVEEDOR", "CÓDIGO PROVEEDOR", "CODIGO", "CÓDIGO", "SKU"];
const ALIAS_DESCRIPCION = ["DESCRIPCION", "DESCRIPCIÓN"];
const ALIAS_DESCRIPCION_PROV = ["DESCRIPCION PROVEEDOR", "DESCRIPCIÓN PROVEEDOR"];
const ALIAS_UNIDAD = ["UNIDAD"];
const ALIAS_CANTIDAD = ["CANTIDAD"];
const ALIAS_PRECIO = ["PRECIO UNITARIO", "PRECIO", "COSTO"];
const ALIAS_COMPRA_MIN = ["COMPRA MINIMA", "COMPRA MÍNIMA", "COMPRA MIN"];
const ALIAS_TIEMPO = ["TIEMPO ENTREGA", "TIEMPO DE ENTREGA"];
const ALIAS_OBSERVACIONES = ["OBSERVACIONES", "COMENTARIOS"];

const obtenerValorColumna = (fila, alias) => {
  const claves = Object.keys(fila);
  for (const texto of alias) {
    const exacta = claves.find((clave) => normalizar(clave) === texto);
    if (exacta !== undefined) return fila[exacta];
  }
  for (const texto of alias) {
    const parcial = claves.find((clave) => normalizar(clave).includes(texto));
    if (parcial !== undefined) return fila[parcial];
  }
  return undefined;
};

const ESTADOS_TAG = {
  ENCONTRADO: { texto: "Encontrado", color: "green" },
  RECIEN_CREADA: { texto: "Relación creada", color: "green" },
  PROPONER: { texto: "Proponer relación", color: "orange" },
  AMBIGUO: { texto: "Ambiguo", color: "gold" },
  NO_ENCONTRADO: { texto: "Sin identificar", color: "red" },
};

/**
 * Carga de la cotizacion que regresa el proveedor, por Excel o por PDF.
 *
 * Estructura clonada de ModalCargarExcelOC (resolver -> proponer relaciones -> crear en
 * lote). Diferencias: acepta PDF ademas de Excel, resuelve contra los insumos de la
 * requisicion y el resultado se guarda como cotizacion, no como lineas de una orden.
 */
class ModalCargarCotizacion extends Component {
  state = {
    proveedorId: null,
    archivoNombre: null,
    origen: null,
    archivoBase64: null,
    cargando: false,
    creandoRelaciones: false,
    guardando: false,
    filas: [],
    // Renglones tal como salieron del archivo, para poder re-resolver si cambia el proveedor
    filasOriginales: [],
    rfcsDetectados: [],
    vigencia: null,
    condicionesPago: "",
    tiempoEntrega: "",
    observaciones: "",
    actualizarCostoRelacion: false,
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({
        proveedorId: this.props.cotizacion?.proveedorId || null,
        archivoNombre: null,
        origen: null,
        archivoBase64: null,
        cargando: false,
        creandoRelaciones: false,
        guardando: false,
        filas: [],
        filasOriginales: [],
        rfcsDetectados: [],
        vigencia: null,
        condicionesPago: "",
        tiempoEntrega: "",
        observaciones: "",
        actualizarCostoRelacion: false,
      });
    }
  }

  /**
   * Al leer un PDF el proveedor puede no detectarse por el RFC. Cuando el usuario lo
   * elige despues, se vuelve a resolver con los mismos renglones en vez de obligarlo a
   * subir el archivo otra vez.
   */
  cambiarProveedor = (proveedorId) => {
    this.setState({ proveedorId }, () => {
      if (proveedorId && this.state.filasOriginales.length > 0) {
        this.setState({ cargando: true }, () => {
          this.resolverFilas(this.state.filasOriginales, this.state.origen);
        });
      }
    });
  };

  get proveedorBloqueado() {
    return !!this.props.cotizacion?.proveedorId;
  }

  archivoABase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  onSubirArchivo = async (info) => {
    const file = info.file.originFileObj || info.file;
    const nombre = file.name || "";

    if (!/\.(xlsx?|pdf)$/i.test(nombre)) {
      message.error("El archivo debe ser un Excel (.xlsx) o un PDF");
      return;
    }
    if (!this.state.proveedorId && /\.xlsx?$/i.test(nombre)) {
      message.error("Selecciona primero el proveedor");
      return;
    }

    this.setState({ cargando: true, archivoNombre: nombre, filas: [] });

    try {
      const base64 = await this.archivoABase64(file);
      this.setState({ archivoBase64: base64 });

      if (/\.pdf$/i.test(nombre)) {
        await this.procesarPdf(file);
      } else {
        await this.procesarExcel(file);
      }
    } catch (err) {
      console.error(err);
      message.error("No se pudo leer el archivo");
      this.setState({ cargando: false });
    }
  };

  procesarExcel = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const primeraHoja = workbook.Sheets[workbook.SheetNames[0]];
          const filasCrudas = XLSX.utils.sheet_to_json(primeraHoja, { defval: "" });

          const filas = filasCrudas
            .map((fila) => ({
              clave: String(obtenerValorColumna(fila, ALIAS_CLAVE) ?? "").trim(),
              codigo: String(obtenerValorColumna(fila, ALIAS_CODIGO) ?? "").trim(),
              descripcion: String(
                obtenerValorColumna(fila, ALIAS_DESCRIPCION) ?? ""
              ).trim(),
              descripcionProveedor: String(
                obtenerValorColumna(fila, ALIAS_DESCRIPCION_PROV) ?? ""
              ).trim(),
              unidad: String(obtenerValorColumna(fila, ALIAS_UNIDAD) ?? "").trim(),
              cantidad: parseNumero(obtenerValorColumna(fila, ALIAS_CANTIDAD)),
              precio: parseNumero(obtenerValorColumna(fila, ALIAS_PRECIO)),
              compraMin: parseNumero(obtenerValorColumna(fila, ALIAS_COMPRA_MIN)),
              tiempoEntrega: String(obtenerValorColumna(fila, ALIAS_TIEMPO) ?? "").trim(),
              observaciones: String(
                obtenerValorColumna(fila, ALIAS_OBSERVACIONES) ?? ""
              ).trim(),
            }))
            .filter((fila) => fila.clave || fila.codigo || fila.descripcion);

          if (filas.length === 0) {
            message.error("No se encontraron renglones reconocibles en el Excel");
            this.setState({ cargando: false });
            return resolve();
          }

          await this.resolverFilas(filas, "excel");
          resolve();
        } catch (err) {
          console.error(err);
          message.error("No se pudo leer el Excel. Verifica que sea un archivo válido.");
          this.setState({ cargando: false });
          resolve();
        }
      };
      reader.readAsArrayBuffer(file);
    });

  procesarPdf = async (file) => {
    const lineas = await extraerLineasDePdf(file);
    const rfcs = detectarRfcs(lineas);
    const partidas = parsearLineasCotizacion(lineas);

    // Si el PDF trae un RFC que corresponde a un proveedor conocido, se selecciona solo
    const proveedorId = this.state.proveedorId || this.proveedorPorRfc(rfcs);

    if (partidas.length === 0) {
      message.warning(
        "No se reconocieron partidas en el PDF. Revisa el archivo o captura los precios a mano."
      );
      this.setState({ cargando: false, rfcsDetectados: rfcs, proveedorId });
      return;
    }

    const filasPdf = partidas.map((valuePartida) => ({ ...valuePartida, clave: "" }));

    if (!proveedorId) {
      message.warning(
        "No se pudo identificar el proveedor por el RFC del PDF: selecciónalo arriba y los renglones se resolverán solos."
      );
      this.setState({
        cargando: false,
        rfcsDetectados: rfcs,
        origen: "pdf",
        filasOriginales: filasPdf,
        filas: filasPdf.map((valuePartida, indice) => ({
          key: indice,
          ...valuePartida,
          estadoMatch: "NO_ENCONTRADO",
        })),
      });
      return;
    }

    this.setState({ rfcsDetectados: rfcs, proveedorId }, () => {
      this.resolverFilas(filasPdf, "pdf");
    });
  };

  proveedorPorRfc = (rfcs) => {
    if (!rfcs || rfcs.length === 0) return null;
    const sugeridos = this.props.proveedoresSugeridos || [];
    const coincidencias = sugeridos.filter((valueProveedor) =>
      rfcs.includes(normalizar(valueProveedor.rfc))
    );
    return coincidencias.length === 1 ? coincidencias[0]._id : null;
  };

  resolverFilas = async (filas, origen) => {
    this.setState({ filasOriginales: filas });
    try {
      const res = await axios.post(
        "cotizacionesRequisicion/resolverPartidasCotizacion",
        {
          empresaId: this.props.empresaId,
          requisicionId: this.props.requisicionId,
          proveedorId: this.state.proveedorId,
          filas,
        },
        { headers: this.props.headersToken }
      );

      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudieron resolver los renglones");
        this.setState({ cargando: false });
        return;
      }

      const filasResueltas = res.data.resultados.map((valueResultado, indice) => {
        const original = filas[indice] || {};
        return {
          key: indice,
          ...original,
          ...valueResultado,
          descripcionProveedor:
            original.descripcionProveedor || valueResultado.descripcion || "",
          noCotiza: false,
          propuesta: valueResultado.propuesta
            ? { incluir: true, ...valueResultado.propuesta }
            : null,
        };
      });

      this.setState({ filas: filasResueltas, cargando: false, origen });
    } catch (err) {
      console.error(err);
      message.error("Error de conexión al resolver los renglones");
      this.setState({ cargando: false });
    }
  };

  cambiarCampo = (indice, campo, valor) => {
    this.setState((prevState) => {
      const filas = [...prevState.filas];
      filas[indice] = { ...filas[indice], [campo]: valor };
      return { filas };
    });
  };

  cambiarPropuesta = (indice, campo, valor) => {
    this.setState((prevState) => {
      const filas = [...prevState.filas];
      filas[indice] = {
        ...filas[indice],
        propuesta: { ...filas[indice].propuesta, [campo]: valor },
      };
      return { filas };
    });
  };

  /**
   * Al elegir el insumo a mano no sabemos si ya existe relacion con este proveedor, asi
   * que el renglon queda como PROPONER con la propuesta precargada: si el usuario no la
   * palomea simplemente se guarda la partida sin relacion.
   */
  elegirInsumo = (indice, insumoId, insumo) => {
    // La unidad SAT sugerida solo viene resuelta para los insumos de la requisicion;
    // si el usuario elige otro del catalogo, la captura a mano en la propuesta.
    const deLaRequisicion = (this.props.insumos || []).find(
      (valueInsumo) => valueInsumo.insumoId.toString() === String(insumoId)
    );

    this.setState((prevState) => {
      const filas = [...prevState.filas];
      const fila = filas[indice];
      filas[indice] = {
        ...fila,
        insumoId,
        insumo,
        relacionId: null,
        estadoMatch: "PROPONER",
        propuesta: {
          incluir: false,
          insumoId,
          codigo: fila.codigo || insumo?.clave || "",
          descripcion: fila.descripcionProveedor || fila.descripcion || insumo?.descripcion || "",
          claveUnidad:
            insumo?.claveUnidad || deLaRequisicion?.claveUnidadSugerida || "",
          costo: fila.precio,
          conversion: 1,
          compraMin: fila.compraMin || 0,
        },
      };
      return { filas };
    });
  };

  crearRelaciones = async () => {
    const propuestas = this.state.filas
      .map((fila, indice) => ({ fila, indice }))
      .filter(({ fila }) => fila.propuesta && fila.propuesta.incluir && fila.insumoId);

    if (propuestas.length === 0) {
      message.error("No hay propuestas seleccionadas para crear");
      return;
    }

    const incompleta = propuestas.find(
      ({ fila }) =>
        !fila.propuesta.codigo ||
        !fila.propuesta.descripcion ||
        !fila.propuesta.claveUnidad ||
        fila.propuesta.costo === undefined ||
        fila.propuesta.costo === null ||
        fila.propuesta.costo === ""
    );
    if (incompleta) {
      message.error(
        "Completa Código, Descripción, Unidad (SAT) y Costo en las propuestas seleccionadas"
      );
      return;
    }

    // asignarProveedorMasivo trabaja por insumo: se deduplica antes de mandar
    const itemsPorInsumo = new Map();
    propuestas.forEach(({ fila }) => {
      const { propuesta } = fila;
      if (itemsPorInsumo.has(propuesta.insumoId)) return;
      itemsPorInsumo.set(propuesta.insumoId, {
        insumoId: propuesta.insumoId,
        codigo: propuesta.codigo,
        descripcion: propuesta.descripcion,
        claveUnidad: propuesta.claveUnidad,
        costo: propuesta.costo,
        conversion: propuesta.conversion,
        compraMin: propuesta.compraMin,
      });
    });

    this.setState({ creandoRelaciones: true });
    try {
      const res = await axios.post(
        "insumos/asignarProveedorMasivo",
        {
          empresaId: this.props.empresaId,
          proveedorId: this.state.proveedorId,
          items: Array.from(itemsPorInsumo.values()),
        },
        { headers: this.props.headersToken }
      );

      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudieron crear las relaciones");
        this.setState({ creandoRelaciones: false });
        return;
      }

      const resultadosPorInsumo = new Map();
      (res.data.resultados || []).forEach((valueResultado) =>
        resultadosPorInsumo.set(String(valueResultado.insumoId), valueResultado)
      );

      let creadas = 0;
      const filas = this.state.filas.map((fila) => {
        if (!fila.propuesta || !fila.propuesta.incluir) return fila;
        const resultado = resultadosPorInsumo.get(String(fila.propuesta.insumoId));
        if (!resultado || !resultado.ok || !resultado.relacion) return fila;
        creadas += 1;
        return {
          ...fila,
          estadoMatch: "RECIEN_CREADA",
          relacionId: resultado.relacion._id,
          propuesta: null,
        };
      });

      this.setState({ filas, creandoRelaciones: false });
      if (creadas > 0) message.success(`${creadas} relación(es) creada(s)`);
    } catch (err) {
      console.error(err);
      message.error("Error de conexión al crear las relaciones");
      this.setState({ creandoRelaciones: false });
    }
  };

  guardar = async () => {
    const { proveedorId, filas } = this.state;

    if (!proveedorId) {
      message.error("Selecciona el proveedor de la cotización");
      return;
    }

    const partidas = filas
      .filter((fila) => fila.insumoId)
      .map((fila) => {
        const insumoRequisicion = (this.props.insumos || []).find(
          (valueInsumo) => valueInsumo.insumoId.toString() === String(fila.insumoId)
        );
        return {
          insumoId: fila.insumoId,
          relacionId: fila.relacionId || null,
          codigoProveedor: fila.codigo,
          descripcionProveedor: fila.descripcionProveedor || fila.descripcion,
          unidadProveedor: fila.unidad,
          cantidadSolicitada: insumoRequisicion?.cantidad,
          cantidad: fila.cantidad,
          precio: fila.precio,
          conversion: fila.propuesta?.conversion,
          compraMin: fila.compraMin,
          tiempoEntrega: fila.tiempoEntrega,
          observaciones: fila.observaciones,
          noCotiza: !!fila.noCotiza,
        };
      });

    if (partidas.length === 0) {
      message.error("No hay renglones identificados que guardar");
      return;
    }

    const archivos = [];
    if (this.state.archivoBase64 && this.state.archivoNombre) {
      archivos.push({ name: this.state.archivoNombre, url: this.state.archivoBase64 });
    }
    // Los archivos ya guardados se reenvian tal cual: sincronizarArchivos borra del
    // disco lo que no venga en la lista.
    (this.props.cotizacion?.archivos || []).forEach((valueArchivo) =>
      archivos.push({ name: valueArchivo.name, url: valueArchivo.url })
    );

    this.setState({ guardando: true });
    try {
      const res = await axios.post(
        "cotizacionesRequisicion/guardarCotizacion",
        {
          empresaId: this.props.empresaId,
          requisicionId: this.props.requisicionId,
          proveedorId,
          cotizacionId: this.props.cotizacion?._id || null,
          partidas,
          archivos,
          vigencia: this.state.vigencia ? this.state.vigencia.toISOString() : null,
          condicionesPago: this.state.condicionesPago,
          tiempoEntrega: this.state.tiempoEntrega,
          observaciones: this.state.observaciones,
          origen: this.state.origen || "manual",
          actualizarCostoRelacion: this.state.actualizarCostoRelacion,
        },
        { headers: this.props.headersToken }
      );

      this.setState({ guardando: false });

      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudo guardar la cotización");
        return;
      }

      message.success(`Cotización ${res.data.numero} guardada`);
      this.props.onGuardado();
    } catch (err) {
      console.error(err);
      this.setState({ guardando: false });
      message.error("Sucedió un error y no se pudo guardar la cotización");
    }
  };

  render() {
    const { visible, onCancel } = this.props;
    const {
      proveedorId,
      filas,
      cargando,
      creandoRelaciones,
      guardando,
      archivoNombre,
    } = this.state;

    const propuestas = filas
      .map((fila, indice) => ({ fila, indice }))
      .filter(({ fila }) => fila.propuesta);
    const propuestasIncluidas = propuestas.filter(
      ({ fila }) => fila.propuesta.incluir
    ).length;
    const identificadas = filas.filter((fila) => fila.insumoId).length;
    const sinIdentificar = filas.length - identificadas;

    const columnas = [
      {
        title: "Código",
        dataIndex: "codigo",
        width: 110,
        render: (valor) => valor || <Text type="secondary">—</Text>,
      },
      {
        title: "Descripción del archivo",
        width: 220,
        render: (_, fila) => fila.descripcionProveedor || fila.descripcion || "",
      },
      {
        title: "Insumo",
        width: 280,
        render: (_, fila) => (
          <SelectInsumosItem
            style={{ width: "100%" }}
            empresaId={this.props.empresaId}
            headersToken={this.props.headersToken}
            tiposInsumos="compra"
            usarSistema
            value={fila.insumoId}
            insumoPreCargado={fila.insumo}
            onChange={(valor, insumo) => this.elegirInsumo(fila.key, valor, insumo)}
            placeholder="Elegir insumo"
          />
        ),
      },
      {
        title: "Cantidad",
        width: 100,
        render: (_, fila) => (
          <InputNumber
            style={{ width: "100%" }}
            value={fila.cantidad}
            onChange={(valor) => this.cambiarCampo(fila.key, "cantidad", valor)}
          />
        ),
      },
      {
        title: "Precio",
        width: 110,
        render: (_, fila) => (
          <InputNumber
            style={{ width: "100%" }}
            value={fila.precio}
            onChange={(valor) => this.cambiarCampo(fila.key, "precio", valor)}
          />
        ),
      },
      {
        title: "Estado",
        width: 200,
        render: (_, fila) => {
          if (fila.estadoMatch === "AMBIGUO" && fila.candidatos?.length) {
            return (
              <Select
                style={{ width: 190 }}
                placeholder="Elegir insumo"
                onChange={(insumoId) =>
                  this.elegirInsumo(
                    fila.key,
                    insumoId,
                    fila.candidatos.find((valueCandidato) => valueCandidato._id === insumoId)
                  )
                }
              >
                {fila.candidatos.map((valueCandidato) => (
                  <Select.Option key={valueCandidato._id} value={valueCandidato._id}>
                    {valueCandidato.clave + " - " + valueCandidato.descripcion}
                  </Select.Option>
                ))}
              </Select>
            );
          }
          const estado = ESTADOS_TAG[fila.estadoMatch] || ESTADOS_TAG.NO_ENCONTRADO;
          return <Tag color={estado.color}>{estado.texto}</Tag>;
        },
      },
      {
        title: "No cotiza",
        width: 80,
        render: (_, fila) => (
          <Checkbox
            checked={fila.noCotiza}
            onChange={(e) => this.cambiarCampo(fila.key, "noCotiza", e.target.checked)}
          />
        ),
      },
    ];

    const columnasPropuestas = [
      {
        title: "",
        width: 40,
        render: (_, { fila, indice }) => (
          <Checkbox
            checked={fila.propuesta.incluir}
            onChange={(e) => this.cambiarPropuesta(indice, "incluir", e.target.checked)}
          />
        ),
      },
      {
        title: "Código",
        width: 110,
        render: (_, { fila, indice }) => (
          <Input
            value={fila.propuesta.codigo}
            onChange={(e) => this.cambiarPropuesta(indice, "codigo", e.target.value)}
          />
        ),
      },
      {
        title: "Descripción",
        render: (_, { fila, indice }) => (
          <Input
            value={fila.propuesta.descripcion}
            onChange={(e) => this.cambiarPropuesta(indice, "descripcion", e.target.value)}
          />
        ),
      },
      {
        title: "Unidad (SAT)",
        width: 110,
        render: (_, { fila, indice }) => (
          <Input
            value={fila.propuesta.claveUnidad}
            onChange={(e) => this.cambiarPropuesta(indice, "claveUnidad", e.target.value)}
          />
        ),
      },
      {
        title: "Costo",
        width: 110,
        render: (_, { fila, indice }) => (
          <InputNumber
            style={{ width: "100%" }}
            value={fila.propuesta.costo}
            onChange={(valor) => this.cambiarPropuesta(indice, "costo", valor)}
          />
        ),
      },
      {
        title: "Conversión",
        width: 100,
        render: (_, { fila, indice }) => (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <InputNumber
              style={{ width: "100%" }}
              value={fila.propuesta.conversion}
              onChange={(valor) => this.cambiarPropuesta(indice, "conversion", valor)}
            />
            <AyudaConversion
              insumo={{ _id: fila.relacionId, conversion: fila.propuesta.conversion }}
            />
          </span>
        ),
      },
      {
        title: "Compra Mín.",
        width: 100,
        render: (_, { fila, indice }) => (
          <InputNumber
            style={{ width: "100%" }}
            value={fila.propuesta.compraMin}
            onChange={(valor) => this.cambiarPropuesta(indice, "compraMin", valor)}
          />
        ),
      },
    ];

    return (
      <ModalItem
        title="Cargar Cotización del Proveedor"
        visible={visible}
        onCancel={onCancel}
        width={1050}
        spinning={cargando || creandoRelaciones || guardando}
        paddingTop
      >
        <div style={{ padding: "0 15px 15px 15px" }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 15 }}
            message="Sube la plantilla de Excel que llenó el proveedor, o su cotización en PDF"
            description="El PDF se lee de forma asistida: revisa siempre los renglones antes de guardar. Si un insumo no tiene relación con este proveedor, se propone crearla."
          />

          <Row gutter={[16, 8]} style={{ marginBottom: 12 }}>
            <Col xs={24} md={10}>
              <Text strong>Proveedor</Text>
              <div>
                <SelectProveedoresItem
                  style={{ width: "100%" }}
                  empresaId={this.props.empresaId}
                  headersToken={this.props.headersToken}
                  disabled={this.proveedorBloqueado}
                  value={proveedorId}
                  onChange={this.cambiarProveedor}
                  placeholder="Seleccione el proveedor"
                />
              </div>
            </Col>
            <Col xs={24} md={14} style={{ paddingTop: 20 }}>
              <Upload
                showUploadList={false}
                customRequest={this.onSubirArchivo}
                accept=".xlsx,.xls,.pdf"
              >
                <ButtonItem icon={<UploadOutlined />}>Subir Excel o PDF</ButtonItem>
              </Upload>
              {archivoNombre && (
                <Text type="secondary" style={{ marginLeft: 10 }}>
                  {archivoNombre}
                </Text>
              )}
            </Col>
          </Row>

          {this.state.rfcsDetectados.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={`RFC detectado(s) en el PDF: ${this.state.rfcsDetectados.join(", ")}`}
            />
          )}

          {filas.length > 0 && (
            <>
              <div style={{ margin: "10px 0" }}>
                <Text>
                  {identificadas} renglón(es) identificado(s)
                  {sinIdentificar > 0 ? `, ${sinIdentificar} sin identificar` : ""}
                </Text>
              </div>
              <Table
                size="small"
                rowKey="key"
                columns={columnas}
                dataSource={filas}
                pagination={{ pageSize: 8 }}
                scroll={{ x: 1000 }}
              />
            </>
          )}

          {propuestas.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 10 }}
                message="Estos insumos no tienen relación con este proveedor"
                description="Revisa los datos y crea las relaciones seleccionadas de una sola vez. Si no las creas, la cotización se guarda igual pero sin quedar relacionada al catálogo del proveedor."
              />
              <Table
                size="small"
                rowKey={({ indice }) => indice}
                columns={columnasPropuestas}
                dataSource={propuestas}
                pagination={false}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <ButtonItem
                  type="primary"
                  loading={creandoRelaciones}
                  disabled={propuestasIncluidas === 0}
                  onClick={this.crearRelaciones}
                >
                  Crear {propuestasIncluidas} relación(es)
                </ButtonItem>
              </div>
            </div>
          )}

          <Row gutter={[16, 8]} style={{ marginTop: 20 }}>
            <Col xs={24} md={6}>
              <Text strong>Vigencia</Text>
              <DatePickerItem
                style={{ width: "100%" }}
                value={this.state.vigencia}
                onChange={(fecha) => this.setState({ vigencia: fecha })}
              />
            </Col>
            <Col xs={24} md={6}>
              <Text strong>Tiempo de entrega</Text>
              <Input
                value={this.state.tiempoEntrega}
                onChange={(e) => this.setState({ tiempoEntrega: e.target.value })}
              />
            </Col>
            <Col xs={24} md={6}>
              <Text strong>Condiciones de pago</Text>
              <Input
                value={this.state.condicionesPago}
                onChange={(e) => this.setState({ condicionesPago: e.target.value })}
              />
            </Col>
            <Col xs={24} md={6}>
              <Text strong>Observaciones</Text>
              <TextAreaItem
                rows={1}
                value={this.state.observaciones}
                onChange={(e) => this.setState({ observaciones: e.target.value })}
              />
            </Col>
          </Row>

          <div style={{ marginTop: 12 }}>
            <Checkbox
              checked={this.state.actualizarCostoRelacion}
              onChange={(e) =>
                this.setState({ actualizarCostoRelacion: e.target.checked })
              }
            >
              Actualizar el costo en la relación del proveedor con estos precios
            </Checkbox>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 15 }}>
            <ButtonItem rojo onClick={onCancel}>
              Cancelar
            </ButtonItem>
            <ButtonItem
              type="primary"
              loading={guardando}
              disabled={identificadas === 0}
              onClick={this.guardar}
            >
              Guardar Cotización ({identificadas})
            </ButtonItem>
          </div>
        </div>
      </ModalItem>
    );
  }
}

export default ModalCargarCotizacion;
